<template>
  <div class="undo-delete-timer-inline ml-3 d-flex align-items-center">
    <div class="timer-circle-small">
      <svg class="timer-svg" viewBox="0 0 36 36">
        <path
          class="timer-bg"
          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
        />
        <path
          class="timer-progress"
          :stroke-dasharray="`${progress}, 100`"
          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
        />
      </svg>
      <span class="timer-text">{{ remainingSeconds }}</span>
    </div>
    <button 
      type="button" 
      class="btn btn-xs btn-outline-primary undo-btn ml-2"
      @click="$emit('cancel')"
    >
      {{ t('messages.cancel') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from '../composables/useI18n'

const { t } = useI18n();

const props = defineProps<{
  duration?: number;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'complete'): void;
}>();

const DURATION = props.duration || 5;

const remainingMs = ref(DURATION * 1000);
const startTime = ref<number>(0);
const animationFrame = ref<number | null>(null);

const remainingSeconds = computed(() => Math.ceil(remainingMs.value / 1000));

const progress = computed(() => {
  const elapsed = DURATION * 1000 - remainingMs.value;
  const percentage = (elapsed / (DURATION * 1000)) * 100;
  return Math.min(percentage, 100);
});

const tick = () => {
  const now = Date.now();
  const elapsed = now - startTime.value;
  remainingMs.value = Math.max(0, DURATION * 1000 - elapsed);
  
  if (remainingMs.value > 0) {
    animationFrame.value = requestAnimationFrame(tick);
  } else {
    emit('complete');
  }
};

onMounted(() => {
  startTime.value = Date.now();
  animationFrame.value = requestAnimationFrame(tick);
});

onUnmounted(() => {
  if (animationFrame.value) {
    cancelAnimationFrame(animationFrame.value);
  }
});
</script>

<style scoped>
.undo-delete-timer-inline {
  display: flex;
  align-items: center;
}

.timer-circle-small {
  position: relative;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.timer-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.timer-bg {
  fill: none;
  stroke: #e9ecef;
  stroke-width: 3;
}

.timer-progress {
  fill: none;
  stroke: #467fcf;
  stroke-width: 3;
  stroke-linecap: round;
  transition: stroke-dasharray 0.1s linear;
}

.timer-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: 600;
  color: #467fcf;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.undo-btn {
  padding: 0 8px;
  height: 28px;
  line-height: 28px;
  white-space: nowrap;
}

@media (prefers-color-scheme: dark) {
  .timer-bg {
    stroke: #4a5568;
  }
  
  .timer-progress {
    stroke: #63b3ed;
  }
  
  .timer-text {
    color: #63b3ed;
  }
}
</style>
